/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text
} from "@mantine/core";
import { useIntegrationApp } from "@membranehq/react";
import { toast } from "sonner";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState, memo } from "react";
import type { HubSpotField } from "../../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";
import type { RelationshipMapping } from "../types";
import { Alert as DsAlert, AlertDescription } from "../../../../../components/ui/alert";

interface RelationshipMappingTableProps {
  slug: string;
  relationshipMappings: RelationshipMapping[];
  onChange: (mappings: RelationshipMapping[]) => void;
  isConnected: boolean;
  hubspotFields: HubSpotField[];
  connectionId?: string;
  usedHubSpotFields?: Set<string>; // Fields already used in field mappings or matcher
  enabledObjects?: string[]; // Objects that have sync configs ready (accounts, companies)
  providerLabel?: string;
  providerKey?: string;
  hideHeader?: boolean;
  persistenceMode?: "component" | "managed";
  reservedProviderFields?: Set<string>;
}

const DEFAULT_RELATIONSHIP_MAPPINGS: Record<string, RelationshipMapping[]> = {
  contacts: [
    { hubspot_list_field: "", display_name: "User-to-Account Relationships", target_object: "accounts" },
    { hubspot_list_field: "", display_name: "User-to-Company Relationships", target_object: "companies" }
  ]
};

const RelationshipMappingTable = ({
  slug,
  relationshipMappings,
  onChange,
  isConnected,
  hubspotFields,
  connectionId,
  usedHubSpotFields = new Set(),
  enabledObjects = [],
  providerLabel = 'HubSpot',
  providerKey = 'hubspot',
  hideHeader = false,
  persistenceMode = "component",
  reservedProviderFields = new Set(),
}: RelationshipMappingTableProps) => {
  const integrationApp = useIntegrationApp();
  const [savingMapping, setSavingMapping] = useState(false);
  const [loadingFieldMapping, setLoadingFieldMapping] = useState(false);
  const initialHydrationRef = useRef(false);

  const normalizedProviderKey = providerKey.toLowerCase();

  const relationshipDisplayNameByTarget: Record<RelationshipMapping['target_object'], string> = {
    accounts: 'User-to-Account Relationships',
    companies: 'User-to-Company Relationships'
  };

  const isListLikeField = (field?: HubSpotField) => {
    if (!field) return false;
    const type = field.type?.toLowerCase();
    const key = field.key?.toLowerCase();
    const title = field.title?.toLowerCase();

    return (
      type?.includes('array') ||
      type?.includes('list') ||
      type === 'enumeration' ||
      key?.includes('list') ||
      key?.includes('array') ||
      title?.includes('list') ||
      title?.includes('array')
    );
  };

  const isSingleValueRelationshipCandidate = (field?: HubSpotField) => {
    if (!field) return false;
    const type = field.type?.toLowerCase();
    const key = field.key?.toLowerCase();

    if (!type && !key) return false;

    // Salesforce stores relationships as single-value references (Id fields)
    if (normalizedProviderKey === 'salesforce') {
      if (type) {
        if (['reference', 'string', 'id', 'lookup'].some(t => type.includes(t))) {
          return true;
        }
      }

      if (key && (key.endsWith('id') || key.endsWith('_id'))) {
        return true;
      }
    }

    return false;
  };

  const relationshipCandidateFields = useMemo(() => {
    if (!Array.isArray(hubspotFields)) return [];

    if (normalizedProviderKey === 'salesforce') {
      // Allow both list fields and single-value reference fields
      return hubspotFields.filter(field => isListLikeField(field) || isSingleValueRelationshipCandidate(field));
    }

    return hubspotFields.filter(field => isListLikeField(field));
  }, [hubspotFields, normalizedProviderKey]);

  const formatFieldLabel = (field?: HubSpotField) => {
    if (!field) return '';
    const label = field.title || field.key;
    const type = field.type || 'unknown';
    return `${label} (${type})`;
  };

  const providerFieldLabel = normalizedProviderKey === 'salesforce'
    ? `${providerLabel} Field`
    : `${providerLabel} List Field`;

  const fallbackRelationshipOptions = useMemo(() => {
    if (relationshipCandidateFields.length > 0) return [];

    if (normalizedProviderKey === 'hubspot') {
      return [
        { value: 'associated_company_ids', label: 'Associated Company IDs' },
        { value: 'associated_contact_ids', label: 'Associated Contact IDs' }
      ];
    }

    return [];
  }, [relationshipCandidateFields.length, normalizedProviderKey]);

  const relationshipHelpText = normalizedProviderKey === 'salesforce'
    ? '💡 This establishes relationships between users and other objects. Select the field that stores the related record ID.'
    : '💡 This establishes relationships between users and other objects. Multiple relationship types can be configured. List fields should contain arrays of related record IDs.';

  const enabledObjectsSignature = useMemo(
    () => [...(enabledObjects || [])].sort().join("|"),
    [enabledObjects],
  );

  const buildMappingsSignature = (mappings: RelationshipMapping[]) => (
    mappings
      .map((mapping) => `${mapping.target_object}:${mapping.hubspot_list_field || ""}`)
      .sort()
      .join("|")
  );

  const relationshipMappingsSignature = useMemo(
    () => buildMappingsSignature(relationshipMappings),
    [relationshipMappings],
  );

  const getUserRecordIdVar = (treatAsList: boolean) => {
    const base = treatAsList ? '$.parent.' : '$.';

    switch (normalizedProviderKey) {
      case 'salesforce':
        return `${base}Id`;
      case 'hubspot':
        return `${base}hs_object_id`;
      default:
        return `${base}id`;
    }
  };

  const hydrateFromIntegrationApp = async () => {
    if (!connectionId || !isConnected || initialHydrationRef.current) return;

    const hasExistingSelection = relationshipMappings.some(mapping => mapping.hubspot_list_field);
    if (hasExistingSelection) {
      initialHydrationRef.current = true;
      return;
    }

    try {
      const fieldMappingInstance = await integrationApp
        .connection(connectionId)
        .fieldMapping(slug)
        .get({ autoCreate: true });

      const relationshipsData: Record<string, any> | undefined = fieldMappingInstance?.importValue?.relationships;
      if (!relationshipsData) {
        initialHydrationRef.current = true;
        return;
      }

      const parsedMappings: RelationshipMapping[] = Object.entries(relationshipsData)
        .map(([relationshipKey]) => {
          const [target, ...fieldParts] = relationshipKey.split('_');
          if (!target || fieldParts.length === 0) return null;

          const targetObject = target as RelationshipMapping['target_object'];
          if (targetObject !== 'accounts' && targetObject !== 'companies') return null;

          const fieldKey = fieldParts.join('_');
          if (!fieldKey) return null;

          return {
            target_object: targetObject,
            hubspot_list_field: fieldKey,
            display_name: relationshipDisplayNameByTarget[targetObject]
          };
        })
        .filter((mapping): mapping is RelationshipMapping => mapping !== null);

      if (parsedMappings.length === 0) {
        initialHydrationRef.current = true;
        return;
      }

      const enabledSet = new Set(enabledObjects);
      const filteredParsedMappings = parsedMappings.filter(mapping => (
        enabledSet.size === 0 || enabledSet.has(mapping.target_object)
      ));

      if (filteredParsedMappings.length === 0) {
        initialHydrationRef.current = true;
        return;
      }

      const existingMappingsByTarget = new Map(
        relationshipMappings.map(mapping => [mapping.target_object, mapping])
      );

      const mergedMappings: RelationshipMapping[] = filteredParsedMappings.map(parsed => {
        const existing = existingMappingsByTarget.get(parsed.target_object);
        if (existing) {
          return {
            ...existing,
            hubspot_list_field: parsed.hubspot_list_field
          };
        }
        return parsed;
      });

      const mergedSignature = buildMappingsSignature(mergedMappings);

      if (mergedSignature !== relationshipMappingsSignature) {
        onChange(mergedMappings);
      }
    } catch (error) {
      console.error('Failed to hydrate relationship mappings from integration app:', error);
    } finally {
      initialHydrationRef.current = true;
    }
  };

  useEffect(() => {
    hydrateFromIntegrationApp().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, isConnected, slug, integrationApp, enabledObjectsSignature, relationshipMappingsSignature]);

  // Get the display mappings, filtering based on enabled objects with sync configs
  const displayMappings: RelationshipMapping[] = (() => {
    if (slug !== 'contacts') {
      return relationshipMappings;
    }

    const defaultRelationships = DEFAULT_RELATIONSHIP_MAPPINGS[slug] || [];
    const mergedMappings: RelationshipMapping[] = [];

    // Filter default relationships to only include enabled objects (with sync configs)
    const availableRelationships = defaultRelationships.filter(defaultRelationship =>
      // Only allow accounts/companies if they have sync configs
      enabledObjects.includes(defaultRelationship.target_object)
    );

    // First, add available default relationships (with existing values if available)
    availableRelationships.forEach(defaultRelationship => {
      const existingMapping = relationshipMappings.find(rm => rm.target_object === defaultRelationship.target_object);

      if (existingMapping) {
        // Use existing mapping (keeps the hubspot_list_field value)
        mergedMappings.push(existingMapping);
      } else {
        // Use default mapping (empty hubspot_list_field)
        mergedMappings.push(defaultRelationship);
      }
    });

    // Add any additional custom relationship mappings that don't match default ones
    // but only if their target object is enabled
    relationshipMappings.forEach(relationshipMapping => {
      const isTargetEnabled = enabledObjects.includes(relationshipMapping.target_object);

      if (isTargetEnabled && !availableRelationships.find(dr => dr.target_object === relationshipMapping.target_object)) {
        mergedMappings.push(relationshipMapping);
      }
    });

    return mergedMappings;
  })();

  const getFieldByKey = (fieldKey?: string) => (
    fieldKey ? hubspotFields.find(field => field.key === fieldKey) : undefined
  );

  // No need for workspace fields - relationships are stored in integration.app only

  const handleRelationshipMappingChange = (index: number, updates: Partial<RelationshipMapping>) => {
    if (!connectionId || !isConnected) return;

    setLoadingFieldMapping(true);
    try {
      // Work with displayMappings for UI, but save back to relationshipMappings
      const updatedMapping = { ...displayMappings[index], ...updates };

      // Update the specific mapping (multiple relationship types can now be active)
      const existingIndex = relationshipMappings.findIndex(rm => rm.target_object === updatedMapping.target_object);

      const newMappings = [...relationshipMappings];
      if (existingIndex >= 0) {
        // Update existing mapping
        newMappings[existingIndex] = updatedMapping;
      } else {
        // Add new mapping
        newMappings.push(updatedMapping);
      }

      onChange(newMappings);
    } finally {
      setLoadingFieldMapping(false);
    }
  };

  const handleRemoveRelationshipMapping = (index: number) => {
    // Remove from displayMappings, but work with relationshipMappings
    const mappingToRemove = displayMappings[index];

    // Remove from actual relationshipMappings array
    const newMappings = relationshipMappings.filter(rm => rm.target_object !== mappingToRemove.target_object);
    onChange(newMappings);
  };

  // Only save to integration.app - no database field mappings needed

  const saveRelationshipMappingsToIntegrationApp = async () => {
    if (!connectionId || !isConnected) return;

    const fieldMappingKey = slug;

    // Get current field mapping
    const fieldMappingInstance = await integrationApp
      .connection(connectionId)
      .fieldMapping(fieldMappingKey)
      .get({
        autoCreate: true
      });

    const currentImportValue = fieldMappingInstance?.importValue || {};

    // Create relationship mapping entries using the specific $jsonata structure
    const relationshipMappingEntries: Record<string, any> = {};

    // Build the relationships structure if we have any valid mappings
    const validMappings = relationshipMappings.filter(mapping =>
      mapping.hubspot_list_field && mapping.target_object
    );

    if (validMappings.length > 0) {
      // Follow the exact $iterate pattern from user's example
      const relationshipsObject: Record<string, any> = {};

      validMappings.forEach((mapping) => {
        const relationshipKey = `${mapping.target_object}_${mapping.hubspot_list_field}`;

        const targetObjectSlug = mapping.target_object === 'accounts' ? 'accounts' : 'companies';
        // User-to-account uses relation_type: "users", user-to-company uses "user-company"
        const relationType = mapping.target_object === 'accounts' ? 'users' : 'user-company';

        const selectedField = getFieldByKey(mapping.hubspot_list_field);
        const treatAsList = isListLikeField(selectedField);
        const userRecordIdVar = getUserRecordIdVar(treatAsList);

        if (treatAsList) {
          relationshipsObject[relationshipKey] = {
            $iterate: {
              source: {
                $var: `$.${mapping.hubspot_list_field}`
              },
              item: {
                relation_type: relationType,
                account_id: {
                  $findAppRecordId: {
                    dataLinkTable: {
                      key: targetObjectSlug
                    },
                    recordId: {
                      $var: "$.item"
                    }
                  }
                },
                user_id: {
                  $findAppRecordId: {
                    dataLinkTable: {
                      key: "contacts"
                    },
                    recordId: {
                      $var: userRecordIdVar
                    }
                  }
                }
              }
            }
          };
        } else {
          relationshipsObject[relationshipKey] = [
            {
              relation_type: relationType,
              account_id: {
                $findAppRecordId: {
                  dataLinkTable: {
                    key: targetObjectSlug
                  },
                  recordId: {
                    $var: `$.${mapping.hubspot_list_field}`
                  }
                }
              },
              user_id: {
                $findAppRecordId: {
                  dataLinkTable: {
                    key: "contacts"
                  },
                  recordId: {
                    $var: userRecordIdVar
                  }
                }
              }
            }
          ];
        }
      });

      relationshipMappingEntries.relationships = relationshipsObject;
      // no-op
    }

    // Preserve existing field mappings and role mappings while replacing relationships
    const preservedMappings: Record<string, any> = {};

    Object.entries(currentImportValue).forEach(([key, value]: [string, any]) => {
      // Preserve role mappings (those with $findAppRecordId pattern)
      if (value?.$findAppRecordId) {
        preservedMappings[key] = value;
      } else if (value?.$var) {
        // Preserve field mappings (those with $var pattern)
        preservedMappings[key] = value;
      }
      // Don't preserve relationships - we're replacing them with new ones
    });

    // Combine preserved mappings with new relationship mappings for clean state
    const updatedImportValue = {
      ...preservedMappings,
      ...relationshipMappingEntries
    };

    // Update the field mapping using put for clean state
    const completeData: any = {
      ...fieldMappingInstance,
      importValue: updatedImportValue
    };

    await integrationApp
      .connection(connectionId)
      .fieldMapping(fieldMappingKey)
      .put(completeData);
  };

  // Save relationship mappings only to integration.app
  const saveRelationshipMappings = async () => {
    if (!connectionId || !isConnected) return;

    setSavingMapping(true);

    // Show loading notification
    toast.loading('Saving relationship mappings...', {
      id: 'saving-relationship-mappings',
      description: 'Updating relationship assignments in integration',
    });

    try {
      await saveRelationshipMappingsToIntegrationApp();

      // Show success notification
      toast.success('Relationship mappings saved!', {
        id: 'saving-relationship-mappings',
        description: 'Relationship assignments have been successfully updated',
      });
    } catch (error) {
      console.error('Failed to save relationship mappings:', error);

      // Show error notification
      toast.error('Failed to save relationship mappings', {
        id: 'saving-relationship-mappings',
        description: 'An error occurred while saving relationship assignments. Please try again.',
      });
    } finally {
      setSavingMapping(false);
    }
  };

  if (!isConnected) {
    return (
      <DsAlert className="border-orange-300 bg-orange-50 px-4 py-3">
        <AlertDescription className="text-xs text-orange-900">
          Connect to {providerLabel} first to configure relationship mappings
        </AlertDescription>
      </DsAlert>
    );
  }

  // Only show for contacts
  if (slug !== 'contacts') {
    return null;
  }

  return (
    <Stack gap="sm">
      {!hideHeader && (
        <>
          <Group justify="space-between">
            <Text fw={500} size="sm">Map object relationships</Text>
          </Group>

          <Text size="xs" c="dimmed">
            Connect relationships between users and other objects. You can configure multiple relationship types simultaneously.
          </Text>
        </>
      )}

      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th><Text size="xs" fw={600}>Target Object</Text></Table.Th>
            <Table.Th><Text size="xs" fw={600}>{providerFieldLabel}</Text></Table.Th>
            <Table.Th w={50} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {displayMappings.map((mapping, index) => (
            <Table.Tr key={`relationship-mapping-${mapping.target_object}-${mapping.hubspot_list_field || 'new'}`}>
              <Table.Td>
                <Text size="xs" fw={500}>{mapping.display_name}</Text>
                <Text size="xs" c="dimmed">{mapping.target_object}</Text>
              </Table.Td>
              <Table.Td>
                <Select
                  placeholder={`Select ${providerFieldLabel}`}
                  data={[
                    ...(() => {
                      const baseOptions = relationshipCandidateFields
                        .filter(field => {
                          const isCurrentSelection = field.key === mapping.hubspot_list_field;
                          if (isCurrentSelection) return true;
                          if (usedHubSpotFields.has(field.key)) return false;
                          if (reservedProviderFields.has(field.key)) return false;
                          return true;
                        })
                        .map(field => ({
                          value: field.key,
                          label: formatFieldLabel(field),
                        }));

                      if (mapping.hubspot_list_field) {
                        const selectedField = getFieldByKey(mapping.hubspot_list_field);
                        if (selectedField && !baseOptions.some(option => option.value === selectedField.key)) {
                          baseOptions.push({
                            value: selectedField.key,
                            label: formatFieldLabel(selectedField),
                          });
                        }
                      }

                      const combinedOptions = [...baseOptions];

                      fallbackRelationshipOptions.forEach(option => {
                        if (!combinedOptions.some(existing => existing.value === option.value)) {
                          combinedOptions.push(option);
                        }
                      });

                      return combinedOptions;
                    })()
                  ]}
                  value={mapping.hubspot_list_field}
                  onChange={(value) => {
                    if (value) {
                      handleRelationshipMappingChange(index, { hubspot_list_field: value });
                    }
                  }}
                  searchable
                  clearable
                  size="xs"
                  rightSection={loadingFieldMapping ? <Loader size="xs" /> : undefined}
                  disabled={loadingFieldMapping}
                />
              </Table.Td>
              <Table.Td>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  size="sm"
                  onClick={() => handleRemoveRelationshipMapping(index)}
                  disabled={DEFAULT_RELATIONSHIP_MAPPINGS[slug]?.some(dm => dm.target_object === mapping.target_object)}
                >
                  <IconTrash size="0.75rem" />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {relationshipMappings.length === 0 && (
        <DsAlert className="border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-gray-0)] px-4 py-3">
          <AlertDescription className="text-xs text-[var(--mantine-color-dimmed)]">
            No relationship mappings configured. Add mappings to connect object relationships between systems.
          </AlertDescription>
        </DsAlert>
      )}

      {persistenceMode === "component" && relationshipMappings.some(m => m.hubspot_list_field) && (
        <Group justify="space-between">
          <DsAlert className="flex-1 border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-gray-0)] px-3 py-2">
            <IconInfoCircle size="0.75rem" className="h-3.5 w-3.5 text-[var(--mantine-color-dimmed)]" />
            <AlertDescription className="text-xs text-[var(--mantine-color-dimmed)]">
              {relationshipHelpText}
            </AlertDescription>
          </DsAlert>
          <Button
            size="xs"
            onClick={() => {
              saveRelationshipMappings().catch(console.error);
            }}
            loading={savingMapping}
            disabled={!relationshipMappings.some(m => m.hubspot_list_field)}
          >
            Save Relationships
          </Button>
        </Group>
      )}
    </Stack>
  );
};

export default memo(RelationshipMappingTable);
